import type { User } from "@supabase/supabase-js";
import { ChevronRightIcon, LoaderCircleIcon } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PROVIDER_LABEL } from "@/lib/constant";
import { selectDisplayName, useAuthStore } from "@/store/authStore";

interface ProfileInfoSectionProps {
  user: User;
}

export function ProfileInfoSection({ user }: ProfileInfoSectionProps) {
  const displayName = useAuthStore(selectDisplayName);
  const [nickname, setNickname] = useState(displayName);
  const [isNicknameDialogOpen, setIsNicknameDialogOpen] = useState(false);
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const updateDisplayName = useAuthStore(state => state.updateDisplayName);

  const email = user.email ?? "";
  const providerId = user.app_metadata?.provider ?? "";
  const providerLabel = PROVIDER_LABEL[providerId] ?? providerId;
  const joinDate = user.created_at
    ? new Date(user.created_at).toLocaleDateString("ko-KR", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  async function handleSaveNickname(): Promise<void> {
    const trimmed = nickname.trim();
    if (!trimmed) {
      toast.error("닉네임을 입력해 주세요.", { richColors: true, position: "top-center" });
      return;
    }

    setIsSavingNickname(true);

    try {
      await updateDisplayName(trimmed);
      setIsNicknameDialogOpen(false);
    } catch {
      toast.error("닉네임 변경에 실패했어요. 다시 시도해 주세요.", { richColors: true, position: "top-center" });
    } finally {
      setIsSavingNickname(false);
    }
  }

  return (
    <div className="rounded-xl bg-card overflow-hidden mb-4">
      {/* 닉네임 */}
      <Dialog
        open={isNicknameDialogOpen}
        onOpenChange={open => {
          setIsNicknameDialogOpen(open);

          if (open) {
            setNickname(displayName);
          }
        }}
      >
        {/* 닉네임 변경 버튼 */}
        <DialogTrigger className="flex w-full items-center justify-between p-4 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors cursor-pointer">
          닉네임
          <div className="flex items-center gap-1.5">
            <p className="text-sm text-muted-foreground">{displayName}</p>
            <ChevronRightIcon className="size-4 text-muted-foreground" />
          </div>
        </DialogTrigger>

        {/* 닉네임 변경 모달 */}
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">닉네임 변경</DialogTitle>
            <DialogDescription>새로운 닉네임을 입력해 주세요.</DialogDescription>
          </DialogHeader>

          {/* 닉네임 입력 필드 */}
          <div className="space-y-1.5">
            <Input
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  handleSaveNickname();
                }
              }}
              placeholder="닉네임"
              maxLength={20}
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-right">{nickname.length}/20</p>
          </div>

          <DialogFooter className="border-t-0 bg-transparent">
            <Button
              type="button"
              size="lg"
              onClick={handleSaveNickname}
              disabled={isSavingNickname}
              className="w-full sm:w-auto"
            >
              {isSavingNickname && <LoaderCircleIcon className="size-4 animate-spin" />}
              저장
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 이메일 */}
      <div className="flex items-center justify-between p-4">
        <span className="text-sm font-medium text-foreground">이메일</span>
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-muted-foreground truncate max-w-48">{email}</span>
        </div>
      </div>

      {/* 로그인 방법 */}
      <div className="flex items-center justify-between p-4">
        <span className="text-sm font-medium text-foreground">로그인 방법</span>
        <span className="text-sm text-muted-foreground">{providerLabel}</span>
      </div>

      {/* 가입 날짜 */}
      {joinDate && (
        <div className="flex items-center justify-between p-4">
          <span className="text-sm font-medium text-foreground">가입 날짜</span>
          <span className="text-sm text-muted-foreground">{joinDate}</span>
        </div>
      )}
    </div>
  );
}
